import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PriceDepositLanding — Page publique caution (Turo-inspired).
 *
 * Design volontairement sobre et neutre : blanc, gris, une seule couleur
 * d'accent noire pour le CTA. Aucune branding cartoon Price — l'objectif
 * est d'inspirer confiance immédiate au client qui reçoit ce lien pour
 * bloquer une caution. Typographie système fine, whitespace généreux,
 * pas d'ombres ni bordures épaisses.
 *
 * Sécurité : aucune donnée bancaire ne transite ici. Le CTA redirige
 * vers la page hébergée Stripe (PCI-compliant). L'endpoint backend
 * renvoie uniquement l'URL Checkout — aucun secret n'est exposé.
 */
export function PriceDepositLanding() {
  const { token } = useParams();
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/price/deposit/by-token/${encodeURIComponent(token || "")}`,
          { method: "GET" },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) {
          // ⚠️ IMPORTANT : le spread doit venir AVANT `status: "ready"` —
          // sinon `data.status` (statut Stripe: created/requires_capture/…)
          // écrase notre marqueur d'état UI et le composant reste blanc.
          // On stocke le statut Stripe dans un champ dédié `paymentStatus`.
          setState({ ...data, paymentStatus: data.status, status: "ready" });
        }
      } catch (e) {
        if (!cancelled) setState({ status: "error", error: String(e) });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-screen bg-white text-neutral-900" style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, Helvetica, Arial, sans-serif" }}>
      <Header />
      <main className="mx-auto max-w-xl px-6 py-10 sm:py-16">
        {state.status === "loading" && <LoadingBlock />}
        {state.status === "error" && <ErrorBlock />}
        {state.status === "ready" && <ReadyBlock deposit={state} />}
      </main>
      <Footer />
    </div>
  );
}

function Header() {
  return (
    <header className="border-b border-neutral-100">
      <div className="mx-auto max-w-xl px-6 py-4 flex items-center justify-between">
        <div className="text-[15px] font-semibold tracking-tight text-neutral-900">Price</div>
        <div className="text-[11px] uppercase tracking-[0.14em] text-neutral-400">Caution sécurisée</div>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-neutral-100 mt-16">
      <div className="mx-auto max-w-xl px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-neutral-400">
        <div>Paiement sécurisé par Stripe</div>
        <a href="mailto:contact@pricecovoit.fr" className="hover:text-neutral-700 transition-colors">
          contact@pricecovoit.fr
        </a>
      </div>
    </footer>
  );
}

function LoadingBlock() {
  return (
    <div className="grid place-items-center py-24" data-testid="deposit-landing-loading">
      <div className="h-6 w-6 rounded-full border-2 border-neutral-200 border-t-neutral-900 animate-spin" />
    </div>
  );
}

function ErrorBlock() {
  return (
    <div className="text-center py-16" data-testid="deposit-landing-error">
      <div className="mx-auto h-10 w-10 rounded-full bg-neutral-100 grid place-items-center">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-5 w-5 text-neutral-500">
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      </div>
      <h1 className="mt-6 text-[22px] font-semibold tracking-tight">Lien invalide</h1>
      <p className="mt-2 text-[14px] text-neutral-500 leading-relaxed max-w-sm mx-auto">
        Ce lien n&apos;est plus valide ou a expiré. Contactez la personne qui vous
        l&apos;a envoyé pour en recevoir un nouveau.
      </p>
    </div>
  );
}

function ReadyBlock({ deposit }) {
  const isPaid = ["requires_capture", "succeeded"].includes(deposit.paymentStatus);
  const isCanceled = deposit.paymentStatus === "canceled";
  const amountFormatted = new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(deposit.amount_eur || 0);

  return (
    <div data-testid="deposit-landing-ready">
      {/* Titre section */}
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.14em] text-neutral-400 mb-3">
          Empreinte carte
        </div>
        <h1 className="text-[28px] sm:text-[32px] font-semibold tracking-tight leading-tight text-neutral-900">
          Autoriser une caution
        </h1>
        <p className="mt-2 text-[15px] text-neutral-500 leading-relaxed">
          Réf. {deposit.rental_ref}
          {deposit.description ? ` · ${deposit.description}` : ""}
        </p>
      </div>

      {/* Montant carte */}
      <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-6 mb-6">
        <div className="text-[12px] text-neutral-500 mb-1">Montant à bloquer</div>
        <div className="text-[36px] font-semibold tracking-tight tabular-nums text-neutral-900">
          {amountFormatted}
        </div>
        <div className="mt-2 text-[13px] text-neutral-500 leading-relaxed">
          Non prélevé — libéré à la fin de la location.
        </div>
      </div>

      {/* CTA / états */}
      {isPaid ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5" data-testid="deposit-landing-already-paid">
          <div className="flex items-start gap-3">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5 text-emerald-700 mt-0.5 flex-none">
              <path d="M20 6L9 17l-5-5" />
            </svg>
            <div>
              <div className="text-[14px] font-semibold text-emerald-900">Caution enregistrée</div>
              <div className="mt-1 text-[13px] text-emerald-800 leading-relaxed">
                L&apos;empreinte est bien en place. Elle sera libérée à la fin de la
                location si aucun incident.
              </div>
            </div>
          </div>
        </div>
      ) : isCanceled ? (
        <div className="rounded-2xl border border-neutral-200 bg-white p-5" data-testid="deposit-landing-canceled">
          <div className="text-[14px] font-semibold text-neutral-900">Caution annulée</div>
          <div className="mt-1 text-[13px] text-neutral-500 leading-relaxed">
            Ce lien a été annulé. Contactez l&apos;équipe pour recevoir un nouveau lien.
          </div>
        </div>
      ) : (
        <a
          href={deposit.checkout_url}
          data-testid="deposit-landing-pay-btn"
          className="block w-full rounded-full bg-neutral-900 py-4 text-center text-[15px] font-semibold text-white transition-colors hover:bg-neutral-700"
        >
          Renseigner ma carte
        </a>
      )}

      {/* Comment ça marche */}
      <div className="mt-10 space-y-4">
        <StepRow
          n="1"
          title="Vous êtes redirigé vers Stripe"
          body="La page de saisie de carte est hébergée par Stripe, pas par Price. Aucune donnée bancaire ne transite par nous."
        />
        <StepRow
          n="2"
          title="Votre carte est bloquée, pas débitée"
          body="Le montant est autorisé sur votre carte. Selon votre banque, il apparaît en attente puis disparaît automatiquement."
        />
        <StepRow
          n="3"
          title="La caution est libérée automatiquement"
          body="À la fin de la location, si tout est en ordre, aucun prélèvement n'est effectué et l'autorisation est levée."
        />
      </div>

      {/* Trust footer */}
      <div className="mt-10 pt-6 border-t border-neutral-100 flex items-center justify-center gap-6 text-[11px] text-neutral-400">
        <span className="flex items-center gap-1.5">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-3.5 w-3.5">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0110 0v4" />
          </svg>
          Chiffré SSL
        </span>
        <span className="flex items-center gap-1.5">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-3.5 w-3.5">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          Stripe · PCI-DSS
        </span>
      </div>
    </div>
  );
}

function StepRow({ n, title, body }) {
  return (
    <div className="flex items-start gap-4">
      <div className="flex-none mt-0.5 h-6 w-6 rounded-full border border-neutral-300 grid place-items-center text-[11px] font-semibold text-neutral-700">
        {n}
      </div>
      <div>
        <div className="text-[14px] font-semibold text-neutral-900 tracking-tight">{title}</div>
        <div className="mt-0.5 text-[13px] text-neutral-500 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}

/**
 * PriceDepositSuccess — Écran retour Stripe (success_url).
 * Même design sobre que le landing.
 */
export function PriceDepositSuccess() {
  const { token } = useParams();
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/price/deposit/by-token/${encodeURIComponent(token || "")}/confirmed`,
          { method: "POST" },
        );
        const data = await res.json().catch(() => ({}));
        if (!cancelled) setStatus(data?.status || "unknown");
      } catch (_) {
        if (!cancelled) setStatus("unknown");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-screen bg-white text-neutral-900" style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, Helvetica, Arial, sans-serif" }}>
      <Header />
      <main
        className="mx-auto max-w-xl px-6 py-16 text-center"
        data-testid="deposit-success-page"
      >
        <div className="mx-auto h-14 w-14 rounded-full bg-emerald-50 border border-emerald-100 grid place-items-center">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-7 w-7 text-emerald-700">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        </div>
        <h1 className="mt-6 text-[28px] font-semibold tracking-tight text-neutral-900">
          Caution enregistrée
        </h1>
        <p className="mt-3 text-[15px] text-neutral-500 leading-relaxed max-w-sm mx-auto">
          {status === "loading" ? (
            "Vérification auprès de Stripe…"
          ) : (
            "L'empreinte de votre carte a bien été prise. Aucun montant n'a été prélevé — la caution sera libérée automatiquement à la fin de la location si tout se passe bien."
          )}
        </p>
        <p className="mt-6 text-[12px] text-neutral-400">
          Un email de confirmation Stripe vous a été envoyé.
        </p>
      </main>
      <Footer />
    </div>
  );
}
